import { Mail, MessageSquare, Phone } from "lucide-react";
import {
  cn,
  contactMailto,
  contactSmsHref,
  contactTelHref,
  formatContactEmail,
  formatContactPhone,
} from "@/lib/utils";

type ContactActionVariant = "buttons" | "links" | "pills";
type ContactActionLayout = "row" | "stack";

interface ContactActionLinksProps {
  phone?: string | null;
  email?: string | null;
  className?: string;
  /** buttons = bordered booking-strip actions; links = footer/text; pills = larger CTA rows */
  variant?: ContactActionVariant;
  /**
   * row = three across (homepage strip).
   * stack = full-width stacked rows (booking sidebar on /stay and /booking).
   */
  layout?: ContactActionLayout;
  /** shell = cream rows; sand = transparent so a sand panel shows through */
  surface?: "shell" | "sand";
  /** Show Call, Text, and/or Email. Defaults to all three. */
  actions?: Array<"call" | "text" | "email">;
  testIdPrefix?: string;
}

/**
 * Public contact actions.
 * Row: label + icon, optional detail under (hidden on small screens).
 * Stack: full-width rows with label left and detail right, so narrow sidebars never overflow.
 */
export function ContactActionLinks({
  phone,
  email,
  className,
  variant = "buttons",
  layout = "row",
  surface = "shell",
  actions = ["call", "text", "email"],
  testIdPrefix = "contact",
}: ContactActionLinksProps) {
  const contactPhone = formatContactPhone(phone);
  const contactEmail = formatContactEmail(email);
  const isStackedVariant = variant === "buttons" || variant === "pills";
  const isStackLayout = isStackedVariant && layout === "stack";

  if (isStackLayout) {
    const rowClass = cn(
      "flex w-full min-w-0 items-center justify-between gap-3 border border-sand-deep",
      surface === "sand" ? "bg-transparent" : "bg-shell",
      "px-3 py-2.5 text-ink font-sans font-semibold uppercase tracking-[0.06em] text-[11px]",
      "hover:bg-ink hover:text-shell hover:border-ink transition-colors",
      variant === "pills" && "rounded-lg",
    );
    const detailClass =
      "min-w-0 truncate text-right font-normal normal-case tracking-normal text-[12px] leading-tight text-current/85";

    return (
      <div className={cn("flex w-full min-w-0 flex-col gap-2", className)}>
        {actions.includes("call") && (
          <a
            href={contactTelHref(contactPhone)}
            className={rowClass}
            data-testid={`${testIdPrefix}-phone-call`}
            aria-label={`Call ${contactPhone}`}
          >
            <span className="inline-flex shrink-0 items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Call</span>
            </span>
            <span className={detailClass}>{contactPhone}</span>
          </a>
        )}
        {actions.includes("text") && (
          <a
            href={contactSmsHref(contactPhone)}
            className={rowClass}
            data-testid={`${testIdPrefix}-phone-text`}
            aria-label={`Text ${contactPhone}`}
          >
            <span className="inline-flex shrink-0 items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Text</span>
            </span>
            <span className={detailClass}>{contactPhone}</span>
          </a>
        )}
        {actions.includes("email") && (
          <a
            href={contactMailto(contactEmail)}
            className={rowClass}
            data-testid={`${testIdPrefix}-email`}
            aria-label={`Email ${contactEmail}`}
          >
            <span className="inline-flex shrink-0 items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              {/* The trailing space is load bearing: without it the visible text
                  reads "Emailhello@..." and stops matching the label. */}
              <span>{"Email "}</span>
            </span>
            <span className={detailClass}>{contactEmail}</span>
          </a>
        )}
      </div>
    );
  }

  const baseClass = isStackedVariant
    ? cn(
        "inline-flex flex-1 flex-col items-center justify-center text-center gap-0.5 border text-ink font-sans font-semibold uppercase transition-colors min-w-0",
        variant === "buttons"
          ? "px-2 py-2 border-sand-deep bg-shell text-[11px] tracking-[0.06em] hover:bg-ink hover:text-shell hover:border-ink"
          : "px-2.5 py-2.5 border-ink bg-transparent text-[12px] tracking-[0.08em] rounded-lg hover:bg-ink hover:text-shell",
      )
    : "inline-flex flex-col items-start gap-0.5 text-[15px] text-ink-soft hover:text-ink transition-colors font-sans group max-w-full min-w-0";

  const iconClass = isStackedVariant
    ? "h-3.5 w-3.5 shrink-0"
    : "h-4 w-4 text-driftwood group-hover:text-ink transition-colors shrink-0";

  const labelRowClass = isStackedVariant
    ? "inline-flex items-center justify-center gap-1.5"
    : "inline-flex items-center gap-2.5";

  const detailClass = isStackedVariant
    ? "hidden lg:block font-normal normal-case tracking-normal text-[11px] leading-tight text-current/80 max-w-full truncate px-0.5"
    : "hidden lg:block text-[15px] leading-tight truncate max-w-full";

  return (
    <div
      className={cn(
        isStackedVariant
          ? "flex w-full min-w-0 flex-nowrap items-stretch justify-center gap-2"
          : "flex flex-col items-start gap-3",
        className,
      )}
    >
      {actions.includes("call") && (
        <a
          href={contactTelHref(contactPhone)}
          className={baseClass}
          data-testid={`${testIdPrefix}-phone-call`}
          aria-label={`Call ${contactPhone}`}
        >
          <span className={labelRowClass}>
            <Phone className={iconClass} aria-hidden="true" />
            <span>Call</span>
          </span>
          <span className={detailClass}>{contactPhone}</span>
        </a>
      )}
      {actions.includes("text") && (
        <a
          href={contactSmsHref(contactPhone)}
          className={baseClass}
          data-testid={`${testIdPrefix}-phone-text`}
          aria-label={`Text ${contactPhone}`}
        >
          <span className={labelRowClass}>
            <MessageSquare className={iconClass} aria-hidden="true" />
            <span>Text</span>
          </span>
          <span className={detailClass}>{contactPhone}</span>
        </a>
      )}
      {actions.includes("email") && (
        <a
          href={contactMailto(contactEmail)}
          className={baseClass}
          data-testid={`${testIdPrefix}-email`}
          aria-label={`Email ${contactEmail}`}
        >
          <span className={labelRowClass}>
            <Mail className={iconClass} aria-hidden="true" />
            <span>{"Email "}</span>
          </span>
          <span className={detailClass}>{contactEmail}</span>
        </a>
      )}
    </div>
  );
}
